import { Capacitor } from '@capacitor/core';
import { Browser } from '@capacitor/browser';
import { Filesystem, Directory } from '@capacitor/filesystem';
import { Share } from '@capacitor/share';

/**
 * Check if running inside a native app (iOS/Android)
 */
export const isNativePlatform = () => Capacitor.isNativePlatform();

/**
 * Get current platform: 'ios', 'android', or 'web'
 */
export const getPlatform = () => Capacitor.getPlatform();

/**
 * Open a URL - uses in-app browser on native, window.open on web
 */
export const openUrl = async (url, target = '_blank') => {
  if (isNativePlatform()) {
    await Browser.open({ url });
  } else {
    window.open(url, target);
  }
};

/**
 * Save and share a file - uses Filesystem+Share on native, file-saver on web
 * @param {Blob} blob - File content as Blob
 * @param {string} fileName - File name with extension
 */
export const saveFile = async (blob, fileName) => {
  if (isNativePlatform()) {
    // Convert blob to base64
    const reader = new FileReader();
    const base64 = await new Promise((resolve, reject) => {
      reader.onload = () => {
        const base64String = reader.result.split(',')[1];
        resolve(base64String);
      };
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });

    // Write to device filesystem
    const result = await Filesystem.writeFile({
      path: fileName,
      data: base64,
      directory: Directory.Cache,
    });

    // Share the file
    await Share.share({
      title: fileName,
      url: result.uri,
    });
  } else {
    // Web: use file-saver
    // file-saver 는 CJS 라 번들러에 따라 saveAs 가 default 아래로 들어온다.
    // 2026-09-16: 데모 번들에서 `const { saveAs } = await import(...)` 가 undefined 가 되어
    // "a is not a function" 으로 죽었다(이 함수는 그때까지 호출처가 0건이라 드러나지 않았다).
    const mod = await import('file-saver');
    const saveAs = mod?.saveAs || mod?.default?.saveAs || mod?.default;
    if (typeof saveAs !== 'function') throw new Error('file-saver 를 불러오지 못했습니다');
    saveAs(blob, fileName);
  }
};
